import React from "react";
import { ArrowLeft, Database, RefreshCw } from "lucide-react";

import { getLiveInstrumentIntraday, getMarketInstrumentProfile, getMarketInstrumentSearch, getMarketSeries } from "../../api/client";
import { Chart } from "../../components/charts";
import { instrumentKlineOption, marketSeriesChartOption } from "./chartOptions";
import { useLatestRequest } from "./useLatestRequest";
import { appendLiveTodayCandle } from "./useIndustrySeriesWithLiveFallback";
import { StockDetailPage } from "./StockDetailPage";
import baseStyles from "./marketObservation.module.css";
import overviewStyles from "./marketOverview.module.css";
import tableStyles from "./marketTables.module.css";

const styles = { ...baseStyles, ...overviewStyles, ...tableStyles };

const INDEX_TYPES = new Set(["domestic_index", "global_index"]);
const INTRADAY_TYPES = new Set(["stock", "domestic_index"]);
const A_SHARE_CODE = /^\d{6}\.(?:SH|SZ|BJ)$/i;

const PERIODS = [
  ["intraday", "分时"],
  ["daily", "日K"],
  ["weekly", "周K"],
  ["monthly", "月K"],
  ["yearly", "年K"],
];

function number(value, digits = 2) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed.toLocaleString("zh-CN", { maximumFractionDigits: digits, minimumFractionDigits: digits }) : "--";
}

function signed(value, digits = 2) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "--";
  return `${parsed > 0 ? "+" : ""}${parsed.toFixed(digits)}%`;
}

function tone(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed === 0) return styles.flat;
  return parsed > 0 ? styles.up : styles.down;
}

function pctChange(points, back) {
  if (points.length <= back) return null;
  const latest = Number(points[points.length - 1].raw);
  const base = Number(points[points.length - 1 - back].raw);
  if (!Number.isFinite(latest) || !Number.isFinite(base) || base === 0) return null;
  return (latest / base - 1) * 100;
}

export function InstrumentDetailPage({ instrumentCode, navigate, onStatus, snapshotId }) {
  const [instrument, setInstrument] = React.useState(null);
  const [headerStats, setHeaderStats] = React.useState(null);
  const [previousClose, setPreviousClose] = React.useState(null);
  const [profile, setProfile] = React.useState(null);
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [period, setPeriod] = React.useState("daily");
  const [seriesPayload, setSeriesPayload] = React.useState(null);
  const [seriesLoading, setSeriesLoading] = React.useState(false);
  const [seriesError, setSeriesError] = React.useState("");
  const resolveGuard = useLatestRequest();
  const seriesGuard = useLatestRequest();

  const resolve = React.useCallback(async () => {
    const requestVersion = resolveGuard.start();
    setLoading(true);
    setError("");
    try {
      const found = await getMarketInstrumentSearch({
        snapshotId,
        query: instrumentCode,
        types: ["stock", "domestic_index", "global_index"],
        limit: 5,
      });
      if (!resolveGuard.isCurrent(requestVersion)) return;
      const match = (found.data.items ?? []).find(
        (item) => item.code.toUpperCase() === instrumentCode.toUpperCase(),
      );
      if (!match) {
        // A watch is resolved from the accounting market database. Its market
        // observation snapshot can lag the stock catalog, but that must not
        // block the basic stock detail page that reads the same tracked code.
        if (A_SHARE_CODE.test(instrumentCode)) {
          setInstrument({
            code: instrumentCode.toUpperCase(),
            name: instrumentCode.toUpperCase(),
            instrument_type: "stock",
          });
          return;
        }
        setInstrument(null);
        setError("没有找到该标的。");
        onStatus({ status: "unavailable", dataDate: null });
        return;
      }
      setInstrument(match);
      const [daily, instrumentProfile] = await Promise.all([
        getMarketSeries({ snapshotId, instrumentIds: [match.instrument_id], period: "daily", rangeDays: 10 }),
        getMarketInstrumentProfile({ snapshotId, code: match.code }),
      ]);
      if (!resolveGuard.isCurrent(requestVersion)) return;
      setProfile(instrumentProfile);
      const points = daily.data.series[0]?.points ?? [];
      const latest = points[points.length - 1];
      setHeaderStats({
        close: points.length ? Number(latest.raw) : null,
        pct_chg: pctChange(points, 1),
        pct_chg_5d: pctChange(points, 5),
        trade_date: points.length ? latest.t : null,
        amount_yi: latest?.amount !== undefined && latest?.amount !== null ? Number(latest.amount) / 100000 : null,
      });
      onStatus({ status: "ok", dataDate: daily.meta.business_date });
      // The header quote is the EOD close above until the live overlay lands;
      // only stock/domestic-index codes have a real-time source (no vendor
      // feed for overseas indices), matching the intraday tab's availability.
      if (INTRADAY_TYPES.has(match.instrument_type) && points.length) {
        const eodClose = Number(latest.raw);
        setPreviousClose(Number.isFinite(eodClose) ? eodClose : null);
        try {
          const live = await getLiveInstrumentIntraday({ code: match.code, isIndex: INDEX_TYPES.has(match.instrument_type) });
          if (!resolveGuard.isCurrent(requestVersion)) return;
          const livePoints = live?.data?.series?.[0]?.points;
          const lastLive = livePoints?.[livePoints.length - 1];
          const livePrice = Number(lastLive?.raw);
          if (Number.isFinite(livePrice) && Number.isFinite(eodClose) && eodClose !== 0) {
            setHeaderStats((prev) => ({
              ...prev,
              close: livePrice,
              pct_chg: (livePrice / eodClose - 1) * 100,
              trade_date: "实时",
            }));
          }
        } catch {
          // keep the EOD close if the live feed is unavailable
        }
      }
    } catch (requestError) {
      if (!resolveGuard.isCurrent(requestVersion)) return;
      setError(requestError.message);
      onStatus({ status: "unavailable", dataDate: null });
    } finally {
      if (resolveGuard.isCurrent(requestVersion)) setLoading(false);
    }
  }, [instrumentCode, onStatus, resolveGuard, snapshotId]);

  React.useEffect(() => { void resolve(); }, [resolve]);

  React.useEffect(() => {
    if (!instrument) return undefined;
    const requestVersion = seriesGuard.start();
    setSeriesLoading(true);
    setSeriesError("");
    (async () => {
      try {
        const data = period === "intraday"
          ? await getLiveInstrumentIntraday({ code: instrument.code, isIndex: INDEX_TYPES.has(instrument.instrument_type) })
          : await getMarketSeries({ snapshotId, instrumentIds: [instrument.instrument_id], period, rangeDays: 240 });
        if (period === "daily" && INTRADAY_TYPES.has(instrument.instrument_type)) {
          await appendLiveTodayCandle(data, instrument.code, "instrument");
        }
        if (seriesGuard.isCurrent(requestVersion)) setSeriesPayload(data);
      } catch (requestError) {
        if (seriesGuard.isCurrent(requestVersion)) {
          setSeriesPayload(null);
          setSeriesError(requestError.message);
        }
      } finally {
        if (seriesGuard.isCurrent(requestVersion)) setSeriesLoading(false);
      }
    })();
  }, [instrument, period, seriesGuard, snapshotId]);

  if (!instrument && loading) {
    return (
      <div className={styles.page} role="status">
        <RefreshCw className={styles.spin} size={22} /><strong>正在读取标的详情</strong>
      </div>
    );
  }
  if (!instrument) {
    return (
      <div className={styles.statePanel} role="alert">
        <Database size={22} /><strong>标的详情暂时无法读取</strong><span>{error}</span>
        <button className={styles.primaryButton} onClick={resolve}>重新读取</button>
      </div>
    );
  }
  const availablePeriods = INTRADAY_TYPES.has(instrument.instrument_type)
    ? PERIODS
    : PERIODS.filter(([value]) => value !== "intraday");
  const isIndex = INDEX_TYPES.has(instrument.instrument_type);

  if (!isIndex) {
    return <StockDetailPage instrument={instrument} navigate={navigate} onStatus={onStatus} snapshotId={snapshotId} />;
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHeading}>
        <div className={styles.headingCopy}>
          <button className={styles.backButton} onClick={() => navigate("/market")}><ArrowLeft size={16} />返回大盘</button>
          <div className={styles.titleRow}>
            <h1>{instrument.name}</h1>
            <span className={styles.metricTag}>{instrument.code}</span>
            <span className={styles.cardHeaderTag}>{isIndex ? "指数" : "个股"}</span>
          </div>
        </div>
      </div>

      {!isIndex ? (
        <section className={styles.instrumentIdentityCard}>
          <div><span>申万行业</span><strong>{profile?.sw_industry_name ?? "—"}</strong></div>
          <div><span>上市日期</span><strong>{profile?.list_date ?? "—"}</strong></div>
        </section>
      ) : null}

      <section className={styles.instrumentQuoteCard}>
        <div className={styles.instrumentQuoteMain}>
          <strong className={tone(headerStats?.pct_chg)}>{number(headerStats?.close)}</strong>
          <span className={tone(headerStats?.pct_chg)}>{signed(headerStats?.pct_chg)}</span>
        </div>
        <div className={styles.instrumentQuoteMeta}>
          <span>5日 <b className={tone(headerStats?.pct_chg_5d)}>{signed(headerStats?.pct_chg_5d)}</b></span>
          <span>成交额 <b>{headerStats?.amount_yi ? `${number(headerStats.amount_yi, 1)}亿` : "--"}</b></span>
          {!isIndex ? (
            <>
              <span>换手率 <b>{profile?.turnover_rate ? `${number(profile.turnover_rate)}%` : "--"}</b></span>
              <span>PE TTM <b>{profile?.pe_ttm ? number(profile.pe_ttm) : "--"}</b></span>
              <span>PB <b>{profile?.pb ? number(profile.pb) : "--"}</b></span>
              <span>总市值 <b>{profile?.total_mv_yi ? `${number(profile.total_mv_yi, 1)}亿` : "--"}</b></span>
              <span>流通市值 <b>{profile?.circ_mv_yi ? `${number(profile.circ_mv_yi, 1)}亿` : "--"}</b></span>
            </>
          ) : null}
          <span>数据截至 {headerStats?.trade_date ?? "--"}</span>
        </div>
      </section>

      <section className={styles.card}>
        <div className={styles.detailChartToolbar}>
          <div className={styles.periodTabs}>
            {availablePeriods.map(([value, label]) => (
              <button key={value} className={period === value ? styles.active : ""} aria-pressed={period === value} onClick={() => setPeriod(value)}>{label}</button>
            ))}
          </div>
        </div>
        {seriesError ? (
          <div className={styles.unavailable}><Database size={18} /><span>{seriesError}</span></div>
        ) : seriesPayload ? (
          <div className={`${styles.detailChart} ${seriesLoading ? styles.chartRefreshing : ""}`} aria-busy={seriesLoading}>
            <Chart option={period === "intraday" ? marketSeriesChartOption(seriesPayload, period, previousClose) : instrumentKlineOption(seriesPayload, period)} />
          </div>
        ) : (
          <div className={styles.statePanel} role="status"><RefreshCw className={styles.spin} size={20} /><strong>正在读取序列</strong></div>
        )}
        <div className={styles.chartEvidence}>
          <Database size={14} />
          {isIndex ? "指数日线" : "个股日线"}
          {!INTRADAY_TYPES.has(instrument.instrument_type) && " · 海外指数暂不支持分时"}
        </div>
      </section>
    </div>
  );
}
